import {
  GridApi,
  gridFilterModelSelector,
  gridPageCountSelector,
  gridPageSelector,
  useGridSelector
} from '@mui/x-data-grid-premium';
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';
import React, { useCallback, useImperativeHandle } from 'react';
import { extractEntityContext, extractPageMetadata } from './entityContextBuilders';
import { buildFormSection } from './formContextBuilders';
import { buildTableSection, formatFilterModel } from './tableContextBuilders';

/**
 * @description Interfaccia esposta via ref per chiamate imperative dal parent
 */
export interface ExportPageContextRef {
  exportContext: () => string;
  getContextObject: () => ContextExportData;
}

/**
 * @description Struttura dati completa del context esportato
 */
export interface ContextExportData {
  title: string;
  url: string;
  entityId: string | null;
  modelMappingHint: string;
  purpose: string | null;
  actions: string | null;
  formFields: readonly string[];
  tableData: string;
  pagination: {
    currentPage: number;
    totalPages: number;
  };
  filters: string;
}

/**
 * @description Props per il componente che espone la funzionalità di export
 */
interface PageContextExporterProps {
  apiRef: React.MutableRefObject<GridApiPremium>;

  onExportReady?: (exportFn: () => string) => void;

  onContextChange?: (context: ContextExportData) => void;

  ref?: React.Ref<ExportPageContextRef>;
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * @description Hook che incapsula tutta la logica di estrazione context
 * @returns Oggetto con funzioni di export e stato corrente
 */
export function usePageContextExport(apiRef: React.MutableRefObject<GridApi>) {
  const currentPage = useGridSelector(apiRef, gridPageSelector);
  const totalPages = useGridSelector(apiRef, gridPageCountSelector);
  const filterModel = useGridSelector(apiRef, gridFilterModelSelector);

  const getContextObject = useCallback((): ContextExportData => {
    const entityContext = extractEntityContext();
    const pageMetadata = extractPageMetadata();
    const formSection = buildFormSection();
    const tableSection = buildTableSection(apiRef, filterModel, currentPage, totalPages);

    return {
      title: document.title,
      url: entityContext.url,
      entityId: entityContext.entityId,
      modelMappingHint: entityContext.modelMappingHint,
      purpose: pageMetadata.purpose,
      actions: pageMetadata.actions,
      formFields: formSection,
      tableData: tableSection,
      pagination: {
        currentPage,
        totalPages
      },
      filters: formatFilterModel(filterModel)
    };
  }, [apiRef, currentPage, filterModel, extractEntityContext, extractPageMetadata, totalPages]);

  const exportAsMarkdown = useCallback(() => {
    const context = getContextObject();

    const sections = [
      `### ${context.title.toUpperCase()}`,
      `URL: ${context.url}`,
      context.entityId && `${context.modelMappingHint}|Possible object_id=${context.entityId}`,
      context.purpose && `PURPOSE: ${context.purpose}`,
      context.actions && `ACTIONS: ${context.actions}`,
      context.formFields.length > 0 && `FORM FIELDS:\n${context.formFields.join('\n')}`,
      context.tableData
    ];

    return sections.filter(Boolean).join('\n');
  }, [getContextObject]);

  return {
    exportAsMarkdown,
    getContextObject
  };
}

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * @description
 * Componente che espone la funzionalità di export context via ref imperative.
 * Non renderizza UI propria - il parent controlla quando eseguire l'export.
 */
export const PageContextExporter = React.forwardRef<ExportPageContextRef, PageContextExporterProps>(
  ({ apiRef, onExportReady, onContextChange }, forwardedRef) => {
    const { exportAsMarkdown, getContextObject } = usePageContextExport(apiRef);

    useImperativeHandle(forwardedRef, () => ({
      exportContext: exportAsMarkdown,
      getContextObject: getContextObject
    }));

    React.useEffect(() => {
      if (onExportReady) {
        onExportReady(exportAsMarkdown);
      }
    }, [onExportReady, exportAsMarkdown]);

    React.useEffect(() => {
      if (onContextChange) {
        onContextChange(getContextObject());
      }
    }, [onContextChange, getContextObject]);

    return null;
  }
);

PageContextExporter.displayName = 'PageContextExporter';
